/**
 * [DT-DAILY 2026-10-16, user] NEW FILE — the "DT Daily Report" tab (after Lead Generator Performance): the Apps Script dashboard
 * "SEO Daily Leads Assignment Report" (Downloads\DT Daily Report\src\DailyReportFrontend.html) in the app — "take all the
 * complete logic and everything how this dashboard looks". Its numbers come from the server (services/dtDailyReport.ts — that
 * dashboard's own counting, checked day by day against it); this page draws them the way that page does:
 *   header     "Daily Leads Assignment Report | US | 16 October 2026" + "Lead assignment & Team Performance Dashboard"; on the
 *              right Download Report (Export PNG) · Date Filter · Switch Department (US | ROW). The app's sign-in and theme take
 *              the place of that page's own login, Logout and Theme button.
 *   section 1  Lead Assignment Report | Digital Marketing Business — Today / This Week / This Month / Previous Month cards: the
 *              date range under each, the total, Email - Freelancer / Email - In-House, and a Daily Average on This Week and
 *              This Month ([DT-DAILY-NO-REACHIQ 2026-10-19, user] no Reach IQ line any more, US or ROW — the total is the two lines)
 *   section 2  Team Manager Performance Report — each manager with its Freelancer / In-House lines (a manager with no leads Today,
 *              This Week or This Month is left out, unless none has any), then the Total line
 *   section 3  Team SEO Advisor Report | the date — a card per Sales team (US: the 4 top cards, the rest centred underneath;
 *              ROW: the Team ROW card only)
 *   download   two PNG images of the picked date, US then ROW — "SEO Daily Report - DT - <date>.png" and "… - ROW - …", without
 *              the controls — as that page's downloadReports, with the same library (html2canvas 1.4.1, loaded only on the click);
 *              both views are drawn off screen for it, so the page on show never changes. [DT-DAILY-PNG-LAYOUT 2026-10-19] drawn
 *              at 1920 px (the tab on a full-HD screen) — wider while any KPI's date would drop under its window — at the old
 *              sharpness (×2560/1440), so a picture is 3413 px wide or more
 *   fresh      follows today (moves to the new day at midnight) unless a past date is picked; asks the server every 4 s whether
 *              the numbers moved and reloads them in place; reloads every minute anyway, and at once when the tab comes back
 *              after a minute — as that page did
 *   analysis   [DT-LEADS-ANALYSIS 2026-10-29, user] "Leads Analysis" left of Download Report → [DT-LEADS-ANALYSIS-SLIDE] the report
 *              slides out to the left and a table comes in from the right: every month (newest first) Assigned Leads (= that
 *              month's This Month card) · Named · No Named · NAL Leads (the NAL word or NAL Accepted, each lead once) · Final Count, for the side of the
 *              page's US | ROW switch; the picked date's month is marked "This Month", the one before "Previous Month". In that
 *              view the header keeps only the US | ROW switch and "Back to Report". Never in the downloaded pictures (they are
 *              drawn from the report part only) — anyone with this tab sees it
 * Colours: that page's own (light: white cards on #f4f6fb with blue #2563eb; dark: graphite #1d2024 on #141619 with blue
 * #3b82f6), text pure black / white; light or dark follows the app's theme. US | ROW slides like the app's other switches.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, DefaultTheme, useTheme } from 'styled-components';
import SingleDateCalendar from '../components/SingleDateCalendar';
import { SkeletonBox } from '../components/Skeleton';
import { LoadFailed } from '../components/LoadState';
import { SlideViewport, SlidePane, slidePos } from '../components/ViewSlide';
import { toastError } from '../components/toastService';
import { InstantPick, PickHandlers } from '../components/InstantPick'; // [DT-MODE-SLIDE 2026-11-02]
import {
  fetchDtDailyReport, fetchDtDailyStatus, fetchDtLeadsAnalysis, DtDailyReport, DtDailyView, DtDailyCard, DtLeadsAnalysis, DtLeadsAnalysisSide,
} from '../services/api';
import { layoutMax, layoutMin, pageRect } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width
import { lgRim, lgPress, lgSurface, lgSquircle, lgCardSheen, lgControlBg, lgControlHover, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]

/** [APP-FIT 2026-10-26] The cards' laptop width rules, on the laid-out width (built once, at load). */
const CARDS_WIDE = layoutMin(901);
const CARDS_NARROW = layoutMax(900);

type Mode = 'DT' | 'ROW';
const MODES: readonly Mode[] = ['DT', 'ROW'];
/** [DT-LEADS-ANALYSIS-SLIDE 2026-10-29] the page's two views: the report, and the Leads Analysis table to its right */
type View = 'report' | 'analysis';
const VIEWS: readonly View[] = ['report', 'analysis'];
const ROW_TEAM = 'team row';
/** The download's sharpness: image pixels per layout pixel (that page's 2560 px picture of a 1440 px layout). */
const EXPORT_SCALE = 2560 / 1440;
/* [DT-DAILY-PNG-LAYOUT 2026-10-19, user] "what i am seeing in the report i want the same layout to the downloaded images, right now in
   the kpi after donwload the date shifts downwards, which is not acceptable, i am okay if the size of the image increase
   horizontally": the pictures were drawn 1440 px wide, where This Week / This Month / Previous Month put their date range on a
   second (third) line. Now drawn as the tab on a full-HD screen — 1920 px, every computer the same — and wider, 80 px at a time,
   while any KPI's date would still drop (a long month like September); the picture keeps the old sharpness, so it only grows wider. */
const EXPORT_LAYOUT_W = 1920;
const EXPORT_MAX_LAYOUT_W = 2880;
const STATUS_MS = 4000;    // that page's VERSION_POLL_MS
const REFRESH_MS = 60000;  // that page's REFRESH_MS

/* ── that page's colours (its :root / [data-theme="dark"] variables) ── */
const LIGHT = { bg: '#f4f6fb', card: '#ffffff', ink: '#000000', line: '#e6e9f0', accent: '#2563eb', accentSoft: '#eef4ff',
  groupBg: '#fcfdff', toggleBg: '#eef1f7', shadow: '0 1px 2px rgba(16,24,40,.06), 0 1px 3px rgba(16,24,40,.08)' };
const DARK = { bg: '#141619', card: '#1d2024', ink: '#ffffff', line: '#30353b', accent: '#3b82f6', accentSoft: 'rgba(59,130,246,.16)',
  groupBg: '#212428', toggleBg: '#23272b', shadow: '0 1px 2px rgba(0,0,0,.5), 0 6px 16px rgba(0,0,0,.35)' };
const pal = (p: { theme: DefaultTheme }) => (p.theme.colorScheme === 'dark' ? DARK : LIGHT);
const num = (x: unknown) => (Number(x) || 0).toLocaleString('en-US');

/* [LIQUID-GLASS 2026-11-02, user] "make the changes in both tabs … for the dt daily report the screenshot will remain the same,
   … changes will be for the app only": the ColorOS glass of the rest of the app (styles/liquidGlass.ts) in this page's own blue,
   on the page ON SCREEN only. The cards are shared with the Download Report pictures (drawn from the hidden Stage), so their
   glass sits under [data-dt-pane]; the buttons, the US | ROW switch, the Leads Analysis table, the "Generating" box and the
   note are never in the pictures. The PNGs stay exactly as they were (checked pixel for pixel). Tables and numbers stay solid. */
type TP = { theme: DefaultTheme };
const dk = (p: TP) => p.theme.colorScheme === 'dark';
/** A glass card: a see-through tint with a shine on top; the rim is the border itself (brighter on the top edge) plus a light
 *  top line and a soft bottom reflection, so it also works on the boxes that scroll sideways on phones (no ::before there). */
const dtGlassCard = css`
  border-color: ${(p: TP) => (dk(p)
    ? 'rgba(255,255,255,0.15) rgba(255,255,255,0.07) rgba(255,255,255,0.10)'
    : 'rgba(16,24,40,0.05) rgba(16,24,40,0.08) rgba(16,24,40,0.10)')};
  background-color: ${(p: TP) => (dk(p) ? 'rgba(255,255,255,0.045)' : 'rgba(255,255,255,0.78)')};
  background-image: ${(p: TP) => (dk(p)
    ? 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0) 45%)'
    : 'linear-gradient(180deg, #ffffff, rgba(255,255,255,0) 60%)')};
  box-shadow: ${(p: TP) => (dk(p)
    ? 'inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -18px 28px -26px rgba(255,255,255,0.24), 0 1px 2px rgba(0,0,0,0.35), 0 16px 34px -18px rgba(0,0,0,0.7)'
    : 'inset 0 1px 0 #ffffff, inset 0 -18px 26px -24px rgba(37,99,235,0.10), 0 1px 2px rgba(16,24,40,0.05), 0 14px 30px -16px rgba(16,24,40,0.20)')};
`;
/** A glass capsule button (Leads Analysis, Download Report, Back to Report, and the Date Filter): rim, shine, a squish and a
 *  soft light where pressed; under the mouse the words turn blue, as before. In light the capsule keeps a thin edge (white
 *  glass alone is lost on the light page). */
const dtGlassButton = css`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border: 1px solid ${(p: TP) => (dk(p) ? 'transparent' : 'rgba(16,24,40,0.09)')};
  border-radius: 999px;
  background-color: ${(p: TP) => (dk(p) ? lgControlBg(p) : 'rgba(255,255,255,0.80)')};
  background-image: ${lgCardSheen};
  box-shadow: ${(p: TP) => (dk(p)
    ? '0 1px 1px rgba(0,0,0,0.25), 0 10px 24px -12px rgba(0,0,0,0.6)'
    : '0 1px 2px rgba(16,24,40,0.06), 0 8px 18px -10px rgba(16,24,40,0.22)')};
  & > * { position: relative; z-index: 1; }
  ${lgRim}
  ${lgPress}
  &:hover:not(:disabled) {
    background-color: ${(p: TP) => (dk(p) ? lgControlHover(p) : '#ffffff')};
    border-color: color-mix(in srgb, ${(p: TP) => pal(p).accent} 55%, transparent);
  }
`;
/** The glossy blue of the picked US | ROW half and the "This Month" tag: a shine on top and a light top edge. */
const dtGlossBlue = (p: TP) => (dk(p)
  ? 'linear-gradient(180deg, rgba(255,255,255,0.30), rgba(255,255,255,0) 55%), linear-gradient(180deg, #4f8ff7, #2f6fe0)'
  : 'linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), linear-gradient(180deg, #3b82f6, #1d5fd8)');

/* The page fills the app's card with its own background; the negative bottom margin covers the card's 88 px dock strip. */
const Page = styled.div`
  min-height: 100%;
  margin-bottom: -88px;
  padding: 24px clamp(16px, 2.5vw / var(--app-zoom, 1), 40px) calc(60px + 88px); /* [APP-FIT 2026-10-26] the window in page px */
  background: ${(p) => pal(p).bg};
  color: ${(p) => pal(p).ink};
  -webkit-font-smoothing: antialiased;
`;
const HeaderTop = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
  .title h1 { margin: 0; font-size: 21px; font-weight: 800; letter-spacing: -0.02em; }
  .title p { margin: 3px 0 0; font-size: 13px; }
`;
const Controls = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  /* [DT-LEADS-ANALYSIS 2026-10-29] phones: the four controls two a line in equal halves — Leads Analysis | Download Report, then
     Date Filter | Switch Department (the laptop row is unchanged) */
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 10px;
    width: 100%;
    & > * { min-width: 0; }
  }
`;
const Ctrl = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  .lbl { font-size: 11px; font-weight: 600; letter-spacing: 0.02em; white-space: nowrap; }
  /* [LIQUID-GLASS 2026-11-02] the Date Filter (the shared calendar's own button, the one with aria-expanded) is the same glass
     capsule as the buttons beside it; its green hover / open ring stays */
  & > button[aria-expanded] {
    ${dtGlassButton}
    padding: 0 14px;
    @media (max-width: 768px) { padding: 0 10px; } /* the calendar's own phone sides: the date fits a half line at 360 px */
    &:hover:not(:disabled) { border-color: ${(p) => p.theme.green600}; }
    &[aria-expanded='true'] {
      border-color: ${(p) => p.theme.green600};
      box-shadow: 0 0 0 3px ${(p) => (dk(p) ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)')};
    }
  }
`;
const DlBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${dtGlassButton}
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 16px;
  color: ${(p) => pal(p).ink};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  svg { width: 15px; height: 15px; display: block; }
  &:hover:not(:disabled) { color: ${(p) => pal(p).accent}; }
  &:disabled { opacity: 0.6; cursor: default; }
  /* [DT-LEADS-ANALYSIS 2026-10-29] phones: the button fills its half of the line */
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    gap: 6px;
    padding: 0 8px;
  }
`;
/* [LIQUID-GLASS 2026-11-02] US | ROW: the track is pressed into the page, the picked half is glossy blue (no squish: the app's
   switches stay quiet) */
const Toggle = styled.div`
  position: relative; /* [DT-MODE-SLIDE 2026-11-02] the knob slides inside it */
  display: inline-flex;
  padding: 3px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: ${(p) => (dk(p) ? 'rgba(0,0,0,0.30)' : 'rgba(16,24,40,0.055)')};
  box-shadow: ${(p) => (dk(p)
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(16,24,40,0.14), inset 0 0 0 1px rgba(16,24,40,0.06), 0 1px 0 rgba(255,255,255,0.9)')};
`;
/* [DT-MODE-SLIDE 2026-11-02, user] "this button should be smooth just like we have the theme toggle switch, right now this just
   switch immidiately": the glossy blue knob is its own layer now and SLIDES between US and ROW with the theme switch's motion
   (0.42 s, the same curve), at the mouse press (ModeSwitch below); the words fade to white / back as it arrives. */
const Seg = styled.button<{ $on: boolean }>`
  position: relative;
  z-index: 1;
  border: 0;
  padding: 7px 20px;
  border-radius: 999px;
  background: transparent;
  color: ${(p) => (p.$on ? '#ffffff' : pal(p).ink)};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: color 240ms cubic-bezier(0.4, 0, 0.2, 1);
`;
const ModeKnob = styled.span<{ $left: number; $width: number; $ready: boolean }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: ${(p) => p.$width}px;
  transform: translateX(${(p) => p.$left}px);
  border-radius: 999px;
  background: ${dtGlossBlue};
  box-shadow: 0 2px 8px ${(p) => (dk(p) ? 'rgba(59,130,246,0.35)' : 'rgba(37,99,235,0.32)')}, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.12);
  opacity: ${(p) => (p.$width ? 1 : 0)};
  pointer-events: none;
  /* off until the knob has been drawn in place once: no slide-in when the page first shows */
  transition: ${(p) => (p.$ready ? 'transform 0.42s cubic-bezier(0.32, 0.72, 0, 1), width 0.42s cubic-bezier(0.32, 0.72, 0, 1)' : 'none')};
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const Section = styled.section`
  margin-top: 26px;
  &:first-child { margin-top: 0; }
  .sec-head { display: flex; align-items: baseline; justify-content: center; gap: 10px; margin: 0 0 12px; }
  .sec-head h2, .sec-head span { margin: 0; font-size: 18px; font-weight: 800; }

  /* [PHONE-LAYOUT 2026-10-23] phones, the page on screen only ([data-dt-pane] — the Download Report image keeps the laptop
     heading): a heading and its "| ..." part read as ONE line that wraps, not two narrow columns side by side */
  @media (max-width: 768px) {
    [data-dt-pane] & .sec-head { display: block; text-align: center; }
    [data-dt-pane] & .sec-head h2, [data-dt-pane] & .sec-head span { display: inline; }
    [data-dt-pane] & .sec-head h2 + span { margin-left: 8px; }
  }
`;
/* [PHONE-LAYOUT 2026-10-23] The narrow-screen columns below (and Kpi's, Cards') are for the page ON SCREEN only
   ([data-dt-pane]): the Download Report image is drawn from the hidden Stage in this same page, so a screen rule used to
   reach it too — made on a phone, its cards came out one a line. The image now keeps the laptop layout on any screen. */
const Kpis = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  [data-dt-pane] & { ${layoutMax(900)} { grid-template-columns: repeat(2, 1fr); } } /* [APP-FIT 2026-10-26] on the laid-out width */
  @media (max-width: 560px) { [data-dt-pane] & { grid-template-columns: 1fr; } }
`;
const Kpi = styled.div`
  position: relative;
  padding: 16px;
  border: 1px solid ${(p) => pal(p).line};
  border-radius: 16px;
  background: ${(p) => pal(p).card};
  box-shadow: ${(p) => pal(p).shadow};
  .khead { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
  .wtop { display: flex; flex-direction: row; align-items: baseline; flex-wrap: wrap; gap: 6px; }
  .win { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
  .wdate { font-size: 11px; font-weight: 600; font-variant-numeric: tabular-nums; }
  .kavg { position: absolute; top: 16px; right: 16px; text-align: right; line-height: 1.2; }
  /* [DT-DAILY] a card with a Daily Average keeps room for it, so a long date range goes to the next line instead of running
     under it (measured at 1366: "- 12 October 2026-18 October 2026" ran under "DAILY AVERAGE") */
  &.avg .wtop { padding-right: 104px; }
  .kavg span { display: block; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
  .kavg b { font-size: 16px; font-weight: 800; font-variant-numeric: tabular-nums; }
  .big { margin: 6px 0 12px; font-size: 30px; font-weight: 800; letter-spacing: -0.02em; }
  .rows { display: flex; flex-direction: column; gap: 7px; }
  .r { display: flex; justify-content: space-between; padding-top: 7px; border-top: 1px dashed ${(p) => pal(p).line}; font-size: 13px; }
  .r b { font-variant-numeric: tabular-nums; }
  /* [PHONE-LAYOUT 2026-10-22] [PHONE-LAYOUT 2026-10-23] the page on screen only (see Kpis) */
  @media (max-width: 768px) { [data-dt-pane] & .kavg span { font-size: 11px; } }
  /* [LIQUID-GLASS 2026-11-02] glass on screen only: the Download Report pictures keep the plain card */
  [data-dt-pane] & { ${lgSquircle(18)} ${dtGlassCard} }
`;
const TableCard = styled.div`
  overflow-x: auto;
  border: 1px solid ${(p) => pal(p).line};
  border-radius: 16px;
  background: ${(p) => pal(p).card};
  box-shadow: ${(p) => pal(p).shadow};
  table { width: 100%; min-width: 520px; border-collapse: collapse; font-size: 13.5px; }
  th, td { padding: 11px 14px; text-align: right; font-variant-numeric: tabular-nums; }
  th:first-child, td:first-child { text-align: left; }
  thead th { background: ${(p) => pal(p).accentSoft}; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    border-bottom: 1px solid ${(p) => pal(p).line}; }
  tbody tr { border-bottom: 1px solid ${(p) => pal(p).line}; }
  tbody tr:last-child { border-bottom: 0; }
  tr.group td { font-weight: 800; background: ${(p) => pal(p).groupBg}; }
  tr.child td:first-child { padding-left: 30px; }
  tr.total td { font-weight: 800; background: ${(p) => pal(p).accentSoft}; }
  /* [LIQUID-GLASS 2026-11-02] a glass frame on screen only (the rows stay solid; the pictures keep the plain frame) */
  [data-dt-pane] & { ${lgSquircle(18)} ${dtGlassCard} }
`;
const Cards = styled.div<{ $bottom?: boolean }>`
  ${(p) => (p.$bottom
    ? 'display: flex; flex-wrap: wrap; justify-content: center; gap: 14px; margin-top: 14px; & > * { flex: 1 1 240px; } ' + CARDS_WIDE + ' { & > * { flex: 0 0 calc((100% - 42px) / 4); } } [data-dt-stage] & > * { flex: 0 0 calc((100% - 42px) / 4); }'
    : 'display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; [data-dt-pane] & { ' + CARDS_NARROW + ' { grid-template-columns: repeat(2, 1fr); } } @media (max-width: 560px) { [data-dt-pane] & { grid-template-columns: 1fr; } }')}
  /* [PHONE-LAYOUT 2026-10-23] the narrow-screen layouts are for the page on screen only; the Download Report image (the
     hidden Stage) always takes the laptop's four a line (see Kpis) */
`;
const TCard = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${(p) => pal(p).line};
  border-radius: 16px;
  background: ${(p) => pal(p).card};
  box-shadow: ${(p) => pal(p).shadow};
  h3 { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; margin: 0; padding: 12px 14px; font-size: 13.5px;
    font-weight: 800; border-bottom: 1px solid ${(p) => pal(p).line}; background: ${(p) => pal(p).accentSoft}; }
  h3 .hcount { font-size: 11px; font-weight: 600; letter-spacing: 0.02em; }
  ul { flex: 1; margin: 0; padding: 6px 0; list-style: none; }
  li { display: flex; justify-content: space-between; padding: 6px 14px; font-size: 13px; }
  li span { font-weight: 500; }
  li b { font-variant-numeric: tabular-nums; }
  .foot { display: flex; justify-content: space-between; padding: 10px 14px; font-weight: 800; border-top: 1px solid ${(p) => pal(p).line};
    background: ${(p) => pal(p).accentSoft}; }
  /* [LIQUID-GLASS 2026-11-02] glass on screen only (the pictures keep the plain card) */
  [data-dt-pane] & { ${lgSquircle(18)} ${dtGlassCard} }
`;
const Busy = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 12, 14, 0.35);
  backdrop-filter: blur(3px);
  /* [LIQUID-GLASS 2026-11-02] the "Generating reports…" box is a glass panel */
  div { position: relative; padding: 16px 22px; ${lgSurface(24)} ${lgSquircle(16)} color: ${(p) => pal(p).ink}; font-size: 14px; font-weight: 700; }
`;
const Note = styled.div`
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 9999;
  max-width: 360px;
  padding: 12px 40px 14px 14px;
  /* [LIQUID-GLASS 2026-11-02] a glass panel; the two links are glossy blue capsules, the close button a round glass button */
  ${lgSurface(30)}
  ${lgSquircle(16)}
  color: ${(p) => pal(p).ink};
  font-size: 13px;
  .t { margin-bottom: 2px; font-weight: 700; }
  .s { font-size: 12px; }
  a { display: inline-block; margin: 8px 6px 0 0; padding: 6px 12px; border-radius: 999px; background: ${dtGlossBlue}; color: #fff;
    font-size: 12px; font-weight: 700; text-decoration: none;
    box-shadow: 0 4px 12px -4px ${(p) => (dk(p) ? 'rgba(59,130,246,0.55)' : 'rgba(37,99,235,0.45)')}, inset 0 1px 0 rgba(255,255,255,0.45); }
  a:hover { filter: brightness(1.06); }
  button {
    position: absolute;
    top: 8px;
    right: 8px;
    isolation: isolate;
    overflow: hidden;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background-color: ${lgControlBg};
    background-image: ${lgCardSheen};
    box-shadow: ${(p) => (dk(p) ? '0 1px 1px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.10)' : '0 1px 2px rgba(16,24,40,0.10), inset 0 0 0 1px rgba(16,24,40,0.08)')};
    color: inherit;
    font-size: 15px;
    line-height: 1;
    cursor: pointer;
    ${lgPress}
    &:hover { background-color: ${lgControlHover}; }
  }
`;
/* The pictures are drawn here — off screen, at a fixed width, never seen. Digits are laid out plain (not tabular) in these
   copies: the image library draws plain digits, so tabular layout left numbers short of their right edge and a gap / no gap
   inside "16 October" (seen in the first images). */
const Stage = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  z-index: -1;
  width: ${EXPORT_LAYOUT_W}px;
  pointer-events: none;
  && * { font-variant-numeric: normal; }
`;

const DL_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/** "Daily Leads Assignment Report | US | 16 October 2026" (that page's updateTitle; DT reads as US). */
const titleOf = (mode: Mode, label: string) => `Daily Leads Assignment Report | ${mode === 'ROW' ? 'ROW' : 'US'}${label ? ` | ${label}` : ''}`;

/** Section 1 — that page's secLeads. [DT-DAILY-NO-REACHIQ 2026-10-19, user] "remove the reachiq line from the cards": the server
 *  sends Email - Freelancer + Email - In-House only (the total = those two), the same on US and ROW. */
const LeadCards: React.FC<{ d: DtDailyReport; v: DtDailyView }> = ({ d, v }) => {
  const la = v.leadAssignment;
  return (
    <Section>
      <div className="sec-head"><h2>Lead Assignment Report</h2><span>| Digital Marketing Business</span></div>
      <Kpis>
        {la.windows.map((w, i) => {
          const basis = i === 1 ? d.avgBiz.week : i === 2 ? d.avgBiz.month : 0;
          return (
            <Kpi key={w} data-kpi={i} className={i === 1 || i === 2 ? 'avg' : undefined}>
              <div className="khead">
                <div className="wtop"><div className="win">{w}</div>{d.windowDates[i] && <div className="wdate">- {d.windowDates[i]}</div>}</div>
                {(i === 1 || i === 2) && <div className="kavg"><span>Daily Average</span><b>{num(basis > 0 ? Math.round(la.totals[i] / basis) : 0)}</b></div>}
              </div>
              <div className="big" data-kpi-total="">{num(la.totals[i])}</div>
              <div className="rows">
                {la.sources.map((s) => <div className="r" key={s.name} data-src={s.name}><span>{s.name}</span><b>{num(s.vals[i])}</b></div>)}
              </div>
            </Kpi>
          );
        })}
      </Kpis>
    </Section>
  );
};

/** Section 2 — that page's secManagers. */
const ManagerTable: React.FC<{ d: DtDailyReport; v: DtDailyView }> = ({ d, v }) => {
  // shown when it has leads Today, This Week or This Month; none has any → all (the table never shrinks to a lone Total)
  const active = v.managers.filter((m) => (Number(m.total[0]) || 0) + (Number(m.total[1]) || 0) + (Number(m.total[2]) || 0) > 0);
  const cells = (a: number[]) => a.map((x, i) => <td key={i}>{num(x)}</td>);
  return (
    <Section>
      <div className="sec-head"><h2>Team Manager Performance Report</h2></div>
      <TableCard>
        <table data-mgr-table="">
          <thead><tr><th>Team Manager</th>{d.windows.map((w) => <th key={w}>{w}</th>)}</tr></thead>
          <tbody>
            {(active.length ? active : v.managers).map((m) => (
              <React.Fragment key={m.name}>
                <tr className="group" data-mgr={m.name}><td>{m.name}</td>{cells(m.total)}</tr>
                <tr className="child"><td>Email - Freelancer</td>{cells(m.freelancer)}</tr>
                <tr className="child"><td>Email - In-House</td>{cells(m.inhouse)}</tr>
              </React.Fragment>
            ))}
            <tr className="total" data-mgr-total=""><td>Total</td>{cells(v.managerTotal)}</tr>
          </tbody>
        </table>
      </TableCard>
    </Section>
  );
};

/** Section 3 — that page's secAdvisors (the server already put each card's people highest first). */
const AdvisorCards: React.FC<{ d: DtDailyReport; mode: Mode }> = ({ d, mode }) => {
  const card = (t: DtDailyCard) => (
    <TCard key={t.team} data-card-team={t.team}>
      <h3><span>{t.team || 'Team'}</span><span className="hcount">Leads Count</span></h3>
      <ul>
        {t.members.length
          ? t.members.map((m) => <li key={m.name} data-advisor={m.name}><span>{m.name}</span><b>{num(m.count)}</b></li>)
          : <li><span>No advisors</span><b>&mdash;</b></li>}
      </ul>
      <div className="foot"><span>Total</span><span data-card-total="">{num(t.total)}</span></div>
    </TCard>
  );
  const isRowTeam = (t: DtDailyCard) => (t.team || '').toLowerCase().trim() === ROW_TEAM;
  const bottom = (d.bottomTeams || []).filter((t) => (mode === 'ROW' ? isRowTeam(t) : !isRowTeam(t)));
  return (
    <Section>
      <div className="sec-head"><h2>Team SEO Advisor Report</h2><span>| {d.todayLabel}</span></div>
      <Cards>{mode === 'ROW' ? null : d.advisorTeams.map(card)}</Cards>
      <Cards $bottom>{bottom.map(card)}</Cards>
    </Section>
  );
};

const ReportBody: React.FC<{ d: DtDailyReport; mode: Mode }> = ({ d, mode }) => {
  const v = mode === 'ROW' ? d.row : d.dt;
  return (<><LeadCards d={d} v={v} /><ManagerTable d={d} v={v} /><AdvisorCards d={d} mode={mode} /></>);
};

/** That page's buildSkeleton: the three sections' shapes while a date loads. */
const SkeletonBody: React.FC = () => (
  <div aria-busy="true" data-dt-skeleton="">
    <Section>
      <div className="sec-head"><SkeletonBox $w="320px" $h="20px" /></div>
      <Kpis>
        {[0, 1, 2, 3].map((i) => (
          <Kpi key={i}>
            <SkeletonBox $w="45%" $h="10px" $mt="2px" /><SkeletonBox $w="60%" $h="28px" $mt="12px" />
            {/* [DT-DAILY-NO-REACHIQ 2026-10-19] two lines, as the loaded card (Freelancer + In-House) */}
            <div className="rows" style={{ marginTop: 14 }}>{[0, 1].map((r) => <SkeletonBox key={r} $h="12px" $mt="6px" />)}</div>
          </Kpi>
        ))}
      </Kpis>
    </Section>
    <Section>
      <div className="sec-head"><SkeletonBox $w="300px" $h="20px" /></div>
      <TableCard style={{ padding: 16 }}>
        <SkeletonBox $w="30%" $h="14px" />
        {[0, 1, 2, 3, 4, 5, 6].map((r) => <SkeletonBox key={r} $h="18px" $mt="10px" />)}
      </TableCard>
    </Section>
    <Section>
      <div className="sec-head"><SkeletonBox $w="280px" $h="20px" /></div>
      <Cards>
        {[0, 1, 2, 3].map((c) => (
          <TCard key={c} style={{ padding: 14 }}>
            <SkeletonBox $w="50%" $h="14px" />
            {[0, 1, 2, 3, 4, 5].map((m) => <SkeletonBox key={m} $h="13px" $mt="9px" />)}
          </TCard>
        ))}
      </Cards>
    </Section>
  </div>
);

type Shot = { url: string; name: string };
const saveFile = (f: Shot) => {
  const a = document.createElement('a');
  a.href = f.url;
  a.download = f.name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => a.remove(), 1000);
};

/* ── [DT-LEADS-ANALYSIS 2026-10-29, user] the Leads Analysis view ─────────────────────────────────────────────────────────────
   "it will show a table for all the leads data … the count for that month and the named lead. And no named lead, and also what
   are the NAL … the NAL and the NAL accepted column … this thing will not be included in the screenshot … whoever has the page
   access for this tab, they can see that" + "one row per month, newest first, with the picked month highlighted".
   [DT-LEADS-ANALYSIS-SLIDE 2026-10-29, user] "when i click on the lead analysis button then the below chart will go to the left
   animation and from the right i will see this table, and in the table view the us row toggle which is there only will remain
   same, and rest all will remove": no popup any more — the report slides out to the left and this table comes in from the
   right (the app's plain view slide); the page's own US | ROW switch drives it (its two tables slide like the report's two
   views); in this view the header keeps only that switch and a way back. The numbers load with the page, so a switch shows no
   loading. */
const AN_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="9" x2="9" y2="21" />
  </svg>
);
const BACK_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
/* laptop: clip, NOT a scroll box, so the header row stays on top while the page scrolls (the ViewSlide lesson); phones: the
   table swipes sideways inside it */
const AnCard = styled.div`
  overflow: clip;
  border: 1px solid ${(p) => pal(p).line};
  border-radius: 16px;
  background: ${(p) => pal(p).card};
  box-shadow: ${(p) => pal(p).shadow};
  /* [LIQUID-GLASS 2026-11-02] glass, as the report's cards (this view is never in the pictures) */
  ${lgSquircle(18)}
  ${dtGlassCard}
  @media (max-width: 768px) {
    overflow: auto hidden;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }
`;
const AnTable = styled.table`
  width: 100%;
  min-width: 680px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
  th, td { padding: 11px 16px; text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; border-bottom: 1px solid ${(p) => pal(p).line}; }
  th:first-child, td:first-child { text-align: left; }
  thead th {
    position: sticky; top: 0; z-index: 1;
    /* the soft blue over the card colour, so the rows never show through the kept header row */
    background: linear-gradient(${(p) => pal(p).accentSoft}, ${(p) => pal(p).accentSoft}), ${(p) => pal(p).card};
    font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  }
  tbody tr:last-child td { border-bottom: 0; }
  td.nal { color: ${(p) => p.theme.danger}; font-weight: 700; }
  td.fin { color: ${(p) => p.theme.greenInk}; font-weight: 800; }
  tr.pick td { background: ${(p) => pal(p).accentSoft}; font-weight: 700; }
  tr.pick td:first-child { box-shadow: inset 3px 0 0 ${(p) => pal(p).accent}; }
  .tag { display: inline-block; margin-left: 6px; padding: 2px 8px; border-radius: 999px; font-size: 10.5px; font-weight: 700;
    letter-spacing: 0.03em; text-transform: uppercase; vertical-align: 1px; }
  /* [LIQUID-GLASS 2026-11-02] glossy capsules: "This Month" glossy blue, "Previous Month" blue-tinted glass */
  .tag.this { background: ${dtGlossBlue}; color: #ffffff; box-shadow: inset 0 1px 0 rgba(255,255,255,0.45), 0 2px 6px -1px ${(p) => (dk(p) ? 'rgba(59,130,246,0.45)' : 'rgba(37,99,235,0.35)')}; }
  .tag.prev {
    border: 1px solid color-mix(in srgb, ${(p) => pal(p).accent} 55%, transparent);
    background: ${(p) => (dk(p) ? 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0) 60%)' : 'linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,255,255,0) 60%)')},
      color-mix(in srgb, ${(p) => pal(p).accent} 10%, transparent);
    color: ${(p) => pal(p).accent};
    box-shadow: inset 0 1px 0 ${(p) => (dk(p) ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.9)')};
  }
  /* phones: the numbers get the screen — the month's tag goes under its name (the month column stays narrow) */
  @media (max-width: 768px) {
    width: max-content;
    min-width: 100%;
    font-size: 13px;
    th, td { padding: 8px 10px; }
    thead th { font-size: 11px; }
    .tag { display: block; width: fit-content; margin: 4px 0 0; font-size: 11px; }
  }
`;
const AnEmpty = styled.div`
  padding: 28px 20px;
  text-align: center;
  font-size: 13.5px;
`;

/* [DT-LEADS-ANALYSIS-NAL-ONE 2026-10-29, user] "make it single 'NAL Leads' … no need for total nal columns also": NAL and NAL
   Accepted are ONE column — every lead with the "NAL" word or "NAL Accepted", each lead once (the server's totalNal) — and no
   Total NAL column; Final Count = Assigned − NAL Leads (unchanged). */
const AN_COLS: { key: keyof DtLeadsAnalysisSide; label: string; cls?: string }[] = [
  { key: 'assigned', label: 'Assigned Leads' },
  { key: 'named', label: 'Named Leads' },
  { key: 'noName', label: 'No Named Leads' },
  { key: 'totalNal', label: 'NAL Leads', cls: 'nal' },
  { key: 'final', label: 'Final Count', cls: 'fin' },
];
/** 'YYYY-MM' → the month before */
const monthBefore = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, '0')}`;
};

/** The Leads Analysis view (to the right of the report). Loads with the page and again whenever the page's numbers move (its
 *  4-s check), so it is ready before the switch; one table per side, sliding with the page's own US | ROW switch. */
const LeadsAnalysisView: React.FC<{ mode: Mode; pickedMonth: string; version: string | null }> = ({ mode, pickedMonth, version }) => {
  const [an, setAn] = useState<DtLeadsAnalysis | null>(null);
  const [failed, setFailed] = useState(false);
  const anRef = useRef(an);
  anRef.current = an;
  const seq = useRef(0);
  const load = useCallback(async () => {
    const n = ++seq.current;
    try {
      const d = await fetchDtLeadsAnalysis();
      if (n !== seq.current) return;
      setAn(d);
      setFailed(false);
    } catch {
      if (n === seq.current && !anRef.current) setFailed(true); // a failed refresh keeps the numbers on show
    }
  }, []);
  useEffect(() => { void load(); }, [version, load]);

  if (failed && !an) return <LoadFailed what="the leads analysis" onRetry={load} />;
  if (an && !an.months.length) return <AnCard><AnEmpty>No assigned leads yet.</AnEmpty></AnCard>;
  const prev = monthBefore(pickedMonth);
  return (
    <SlideViewport>
      {MODES.map((m) => {
        const key = m === 'ROW' ? 'row' : 'us';
        return (
          <SlidePane key={m} $pos={slidePos(mode, m, MODES)} $quiet $flat $nested aria-hidden={mode !== m} data-an-pane={m}>
            <AnCard>
              <AnTable data-an-table="" aria-busy={!an}>
                <thead><tr><th>Month</th>{AN_COLS.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
                <tbody>
                  {an ? an.months.map((mo) => {
                    const s = mo[key];
                    const tag = mo.month === pickedMonth ? 'this' : mo.month === prev ? 'prev' : '';
                    return (
                      <tr key={mo.month} className={tag === 'this' ? 'pick' : undefined} data-an-month={mo.month}>
                        <td>{mo.label}{tag && <>{' '}<span className={`tag ${tag}`}>{tag === 'this' ? 'This Month' : 'Previous Month'}</span></>}</td>
                        {AN_COLS.map((c) => <td key={c.key} className={c.cls} data-an-col={c.key}>{num(s[c.key])}</td>)}
                      </tr>
                    );
                  }) : [0, 1, 2, 3, 4, 5, 6, 7].map((r) => (
                    <tr key={r}>{[0, ...AN_COLS.map((_, i) => i + 1)].map((c) => <td key={c}><SkeletonBox $w={c ? '48px' : '120px'} $h="14px" style={{ marginLeft: c ? 'auto' : 0 }} /></td>)}</tr>
                  ))}
                </tbody>
              </AnTable>
            </AnCard>
          </SlidePane>
        );
      })}
    </SlideViewport>
  );
};

/** [DT-MODE-SLIDE 2026-11-02] US | ROW: the knob sits under the picked half (measured, so US and ROW keep their own widths)
 *  and slides to the other one at the mouse press (components/InstantPick — touch and keys: the click); the report follows. */
const ModeTrack: React.FC<{ shown: Mode; press: (m: Mode) => PickHandlers }> = ({ shown, press }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const segRefs = useRef<Record<Mode, HTMLButtonElement | null>>({ DT: null, ROW: null });
  const [knob, setKnob] = useState({ left: 0, width: 0 });
  const [ready, setReady] = useState(false);
  const measure = useCallback(() => {
    const el = segRefs.current[shown];
    if (el) setKnob((k) => (k.left === el.offsetLeft && k.width === el.offsetWidth ? k : { left: el.offsetLeft, width: el.offsetWidth }));
  }, [shown]);
  useLayoutEffect(() => { measure(); }, [measure]);
  // the words can change size (the font arriving): measure again
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !wrapRef.current) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [measure]);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <Toggle ref={wrapRef} role="group" aria-label="Switch Department" data-dt-mode="">
      <ModeKnob $left={knob.left} $width={knob.width} $ready={ready} aria-hidden="true" data-dt-mode-knob="" />
      {MODES.map((m) => (
        <Seg key={m} ref={(el: HTMLButtonElement | null) => { segRefs.current[m] = el; }} type="button" $on={shown === m} aria-pressed={shown === m}
          {...press(m)} data-mode={m}>{m === 'ROW' ? 'ROW' : 'US'}</Seg>
      ))}
    </Toggle>
  );
};
const ModeSwitch: React.FC<{ mode: Mode; onPick: (m: Mode) => void }> = ({ mode, onPick }) => (
  <InstantPick value={mode} onPick={onPick}>{(shown, press) => <ModeTrack shown={shown} press={press} />}</InstantPick>
);

const DtDailyReportPage: React.FC = () => {
  const isDark = (useTheme() as DefaultTheme).colorScheme === 'dark';
  const [data, setData] = useState<DtDailyReport | null>(null);
  const [failed, setFailed] = useState(false);
  const [want, setWant] = useState<string | null>(null);   // a past date picked; null = follow today
  const [mode, setMode] = useState<Mode>('DT');
  const [exportOf, setExportOf] = useState<{ d: DtDailyReport; dark: boolean } | null>(null);
  const [note, setNote] = useState<Shot[] | null>(null);
  const [view, setView] = useState<View>('report'); // [DT-LEADS-ANALYSIS-SLIDE] the report, or the Leads Analysis table
  const wantRef = useRef(want);
  wantRef.current = want;
  const dataRef = useRef(data);
  dataRef.current = data;
  const exportingRef = useRef(false);
  exportingRef.current = !!exportOf;
  const seq = useRef(0);
  const lastLoadAt = useRef(0);

  /** silent = a background refresh: what is on screen stays until the new numbers land; a failure keeps it. */
  const load = useCallback(async (silent: boolean) => {
    const n = ++seq.current;
    const date = wantRef.current;
    if (!silent) setFailed(false);
    try {
      const d = await fetchDtDailyReport(date || undefined);
      if (n !== seq.current) return;           // only the newest request may land
      setData(d);
      setFailed(false);
      lastLoadAt.current = Date.now();
    } catch {
      if (n !== seq.current || silent) return;
      setFailed(true);
    }
  }, []);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => { void loadRef.current(false); }, []);

  // every 4 s (tab on show): did the numbers move, or did today move on? → reload in place
  useEffect(() => {
    let busy = false;
    const t = window.setInterval(async () => {
      if (busy || document.visibilityState !== 'visible' || exportingRef.current) return;
      const cur = dataRef.current;
      if (!cur) return;
      busy = true;
      try {
        const s = await fetchDtDailyStatus();
        const latest = dataRef.current;
        if (latest && (s.version !== latest.version || (!wantRef.current && s.today !== latest.anchorDate))) void loadRef.current(true);
      } catch { /* a missed check: the next one asks again */ } finally { busy = false; }
    }, STATUS_MS);
    return () => window.clearInterval(t);
  }, []);
  // every minute anyway, and at once when the tab comes back after a minute
  useEffect(() => {
    const due = () => Date.now() - lastLoadAt.current >= REFRESH_MS;
    const t = window.setInterval(() => { if (due() && !exportingRef.current) void loadRef.current(true); }, 30000);
    const onVis = () => { if (document.visibilityState === 'visible' && due() && !exportingRef.current) void loadRef.current(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  /** That page's onDateChange: the latest date (today) keeps following today; a past date stays put. */
  const onPickDay = (v: string) => {
    const next = !v || v === dataRef.current?.today ? null : v;
    if (next === wantRef.current && (next ?? dataRef.current?.today) === dataRef.current?.anchorDate) return;
    wantRef.current = next;
    setWant(next);
    void load(false);
  };

  const shownDate = want ?? data?.today ?? '';
  // the numbers on show belong to the date in the box (else the skeleton, while the new date loads)
  const shown = data && (want ? data.anchorDate === want : data.anchorDate === data.today) ? data : null;

  // ── Download Report: the two pictures, drawn off screen (that page's downloadReports) ──
  const sheetRefs = useRef<Record<Mode, HTMLDivElement | null>>({ DT: null, ROW: null });
  const stageRef = useRef<HTMLDivElement | null>(null); // [DT-DAILY-PNG-LAYOUT] the off-screen stage (its width grows if a date drops)
  const onDownload = () => { if (shown && !exportOf) setExportOf({ d: shown, dark: isDark }); };
  useEffect(() => {
    if (!exportOf) return undefined;
    let cancelled = false;
    const made: Shot[] = [];
    (async () => {
      try {
        const fonts = (document as any).fonts;
        if (fonts?.ready) await fonts.ready;
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        // [DT-DAILY-PNG-LAYOUT] every KPI's date on its window's line, as on the tab: the stage grows until none drops (US and ROW)
        const stage = stageRef.current;
        const drops = () => [...(stage?.querySelectorAll<HTMLElement>('[data-kpi] .wtop') ?? [])].some((t) => {
          const win = t.querySelector<HTMLElement>('.win');
          return !!win && t.getBoundingClientRect().height > win.getBoundingClientRect().height * 1.5;
        });
        for (let w = EXPORT_LAYOUT_W; stage && w < EXPORT_MAX_LAYOUT_W && drops();) {
          w = Math.min(EXPORT_MAX_LAYOUT_W, w + 80);
          stage.style.width = `${w}px`;
        }
        const { default: html2canvas } = await import('html2canvas');
        for (const m of MODES) {
          const el = sheetRefs.current[m];
          if (!el) throw new Error('the report did not draw');
          const r = pageRect(el); // [APP-FIT 2026-10-26] page px: the picture is drawn at the page's own size
          const scale = Math.min(EXPORT_SCALE, 16000 / (r.height || 1));
          /* [APP-FIT 2026-10-26] html2canvas draws from a copy of the page. Under the app zoom (utils/appZoom) it mixed zoomed
             boxes with unzoomed font sizes and the letters ran into each other, so the copy is drawn unzoomed: the same
             picture as on a 1920 px screen, and the page on screen never changes. */
          const unzoom = (doc: Document) => { doc.documentElement.style.removeProperty('zoom'); doc.documentElement.style.setProperty('--app-zoom', '1'); };
          const canvas = await html2canvas(el, { backgroundColor: exportOf.dark ? DARK.bg : LIGHT.bg, scale, useCORS: true, logging: false, onclone: unzoom });
          const blob = await new Promise<Blob>((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('no image'))), 'image/png'));
          made.push({ url: URL.createObjectURL(blob), name: `SEO Daily Report - ${m} - ${exportOf.d.todayLabel}.png` });
        }
        if (cancelled) return;
        saveFile(made[0]);
        window.setTimeout(() => saveFile(made[1]), 400);
        setNote(made);
      } catch (e: any) {
        if (!cancelled) toastError(`Could not make the report images. ${e?.message || ''}`.trim());
        made.forEach((f) => URL.revokeObjectURL(f.url));
      } finally {
        if (!cancelled) setExportOf(null);
      }
    })();
    return () => { cancelled = true; };
  }, [exportOf]);
  // the note offers the two files again for 20 s (a browser may hold back the second automatic download)
  useEffect(() => {
    if (!note) return undefined;
    const t = window.setTimeout(() => setNote(null), 20000);
    return () => { window.clearTimeout(t); note.forEach((f) => URL.revokeObjectURL(f.url)); };
  }, [note]);

  const header = useMemo(() => (
    <div className="title">
      <h1 data-dt-title="">{view === 'analysis' ? `Leads Analysis | ${mode === 'ROW' ? 'ROW' : 'US'}` : titleOf(mode, shown?.todayLabel || '')}</h1>
      <p>Lead assignment &amp; Team Performance Dashboard</p>
    </div>
  ), [mode, shown, view]);

  return (
    <Page data-dt-daily="">
      <HeaderTop>
        {header}
        <Controls>
          {view === 'analysis' ? (
            /* [DT-LEADS-ANALYSIS-SLIDE] the table view keeps only the US | ROW switch — and this way back to the report */
            <Ctrl>
              <DlBtn type="button" onClick={() => setView('report')} data-dt-back="">{BACK_ICON}<span>Back to Report</span></DlBtn>
              <span className="lbl">Daily Report</span>
            </Ctrl>
          ) : (
            <>
              <Ctrl>
                <DlBtn type="button" onClick={() => setView('analysis')} data-dt-analysis="">{AN_ICON}<span>Leads Analysis</span></DlBtn>
                <span className="lbl">Full Report</span>
              </Ctrl>
              <Ctrl>
                <DlBtn type="button" onClick={onDownload} disabled={!shown || !!exportOf} data-dt-download="">{DL_ICON}<span>Download Report</span></DlBtn>
                <span className="lbl">Export PNG</span>
              </Ctrl>
              <Ctrl>
                <SingleDateCalendar value={shownDate} min={data?.firstDay || null} max={data?.today || null} onChange={onPickDay} hint={false} />
                <span className="lbl">Date Filter</span>
              </Ctrl>
            </>
          )}
          <Ctrl>
            <ModeSwitch mode={mode} onPick={setMode} /* [DT-MODE-SLIDE 2026-11-02] the knob slides */ />
            <span className="lbl">Switch Department</span>
          </Ctrl>
        </Controls>
      </HeaderTop>
      {/* [DT-LEADS-ANALYSIS-SLIDE] Leads Analysis sits to the RIGHT of the report: the report slides out to the left as the table
          comes in from the right (Back reverses it); both stay mounted, so neither reloads on a switch */}
      <SlideViewport>
        <SlidePane $pos={slidePos(view, 'report', VIEWS)} $quiet $flat aria-hidden={view !== 'report'} data-dt-view="report">
          {failed && !shown ? (
            <LoadFailed what="the DT Daily Report" onRetry={() => load(false)} />
          ) : (
            <SlideViewport>
              {MODES.map((m) => (
                <SlidePane key={m} $pos={slidePos(mode, m, MODES)} $quiet $flat $nested aria-hidden={mode !== m} data-dt-pane={m}>
                  {shown ? <ReportBody d={shown} mode={m} /> : <SkeletonBody />}
                </SlidePane>
              ))}
            </SlideViewport>
          )}
        </SlidePane>
        <SlidePane $pos={slidePos(view, 'analysis', VIEWS)} $quiet $flat aria-hidden={view !== 'analysis'} data-dt-view="analysis">
          <LeadsAnalysisView mode={mode} pickedMonth={shownDate.slice(0, 7)} version={data?.version ?? null} />
        </SlidePane>
      </SlideViewport>
      {exportOf && createPortal(
        <>
          <Stage ref={stageRef} aria-hidden="true" data-dt-stage="">
            {MODES.map((m) => (
              <Page key={m} ref={(el: HTMLDivElement | null) => { sheetRefs.current[m] = el; }} style={{ minHeight: 0, margin: 0, padding: '24px 40px 40px' }} data-dt-sheet={m}>
                <HeaderTop><div className="title"><h1>{titleOf(m, exportOf.d.todayLabel)}</h1><p>Lead assignment &amp; Team Performance Dashboard</p></div></HeaderTop>
                <ReportBody d={exportOf.d} mode={m} />
              </Page>
            ))}
          </Stage>
          <Busy role="status"><div>Generating reports…</div></Busy>
        </>,
        document.body,
      )}
      {note && createPortal(
        <Note role="status" data-dt-note="">
          <div className="t">DT &amp; ROW reports are downloading</div>
          <div className="s">Not in your Downloads folder? Save them here:</div>
          <a href={note[0].url} download={note[0].name}>DT PNG</a>
          <a href={note[1].url} download={note[1].name}>ROW PNG</a>
          <button type="button" aria-label="Close" onClick={() => setNote(null)} data-lg-press="">&times;</button>
        </Note>,
        document.body,
      )}
    </Page>
  );
};

export default DtDailyReportPage;
